import React from 'react';

import Inspector from '../../components/Inspector';
import {Theme} from '../../pages/Benchmark/benchmarkTypes';
import {getTheme} from '../../pages/Benchmark/Theme';
import * as ViewerHooks from '../../state/viewer/hooks';
import {BenchmarkProject} from '../../types/graphql';
import {AutoSyncPanelBankContextProvider} from '../PanelBank/AutoSyncPanelBankContext';
import {DefinedMetricContextProvider} from '../PanelBank/DefinedMetricContext';
import {PanelBankSectionsScrollContextProvider} from '../PanelBank/PanelBankSectionsScrollContextProvider';
import {PanelBankWidthContextProvider} from '../PanelBank/PanelBankWidthContext';
import {WandbConfigContextProvider} from '../PanelBank/WandbConfigContext';
import RunSelector from '../RunSelector';
import {PROJECT_READONLY_WORKSPACE_RUN_SELECTOR_DISABLED_FEATURES} from '../RunSelector/util';
import {PanelsQuickAddContextProvider} from '../WorkspaceDrawer/PanelsQuickAdd/PanelsQuickAddContext';
import {WorkspaceDrawer} from '../WorkspaceDrawer/WorkspaceDrawer';
import {RunSelectorResizingContextProvider} from './RunSelectorResizingContext';
import {MultiRunWorkspaceAllProps} from './types';

type WorkspaceWithRunSelectorProps = {
  children?: React.ReactNode;
  isProjectReadOnly: boolean;
  isSingleMode: boolean;
  isTableExpanded: boolean;
  onSelectRunName: MultiRunWorkspaceAllProps['onSelectRunName'];
  onSetTableExpanded: MultiRunWorkspaceAllProps['onSetTableExpanded'];
  panelBankWidth: number;
  project: MultiRunWorkspaceAllProps['project'];
  recommendedGrouping: MultiRunWorkspaceAllProps['recommendedGrouping'];
  runSetRef: MultiRunWorkspaceAllProps['runSetRef'];
  sectionPart: MultiRunWorkspaceAllProps['sectionPart'];
  selectedRunName: string | undefined;
  setPanelBankWidth: (width: number) => void;
  shouldShowRunSelector: boolean;
  showArtifactCounts: boolean;
  showLogCounts: boolean;
  title: string | React.ReactElement;
};

export const WorkspaceWithRunSelector = ({
  children,
  isProjectReadOnly,
  isSingleMode,
  isTableExpanded,
  onSelectRunName,
  onSetTableExpanded,
  panelBankWidth,
  project,
  recommendedGrouping,
  runSetRef,
  sectionPart,
  selectedRunName,
  setPanelBankWidth,
  shouldShowRunSelector,
  showArtifactCounts,
  showLogCounts,
  title,
}: WorkspaceWithRunSelectorProps) => {
  const viewer = ViewerHooks.useViewer();

  const linkedBenchmarkMeta: BenchmarkProject | null =
    !project.isBenchmark && project.linkedBenchmark != null
      ? project.linkedBenchmark
      : null;

  const linkedBenchmarkInfo: Theme | null =
    linkedBenchmarkMeta != null ? getTheme(linkedBenchmarkMeta) : null;

  return (
    <RunSelectorResizingContextProvider>
      {shouldShowRunSelector && (
        <RunSelector
          analyticsLocation={isTableExpanded ? 'runs-table' : 'workspace'}
          benchmarkMeta={project.linkedBenchmark}
          className="single-wb-table"
          customRunColorsRef={sectionPart.customRunColorsRef}
          customRunNamesRef={sectionPart.customRunNamesRef}
          disabledFeatures={
            isProjectReadOnly
              ? PROJECT_READONLY_WORKSPACE_RUN_SELECTOR_DISABLED_FEATURES
              : undefined
          }
          displayBenchmark={linkedBenchmarkInfo != null}
          isExpandable
          isExpanded={isTableExpanded}
          isSingleMode={isSingleMode}
          // Make sure to remount if the runSetRef id changes (happens when we load a new workspace, or clear our workspace)
          key={runSetRef.id}
          // Unfortunately, `RunSelector` has a `readOnly` prop that means something completely different
          // (it doesn't let the user set filters, grouping, etc.).
          // Instead, this `readOnly` variable refers to the current viewer's access to the project
          // (whether or not they can rename/delete runs, etc.), so we pass it in to `RunSelector` as the `noEdit` prop instead.
          onSelectRunName={onSelectRunName}
          onSetExpanded={onSetTableExpanded}
          recommendedGrouping={recommendedGrouping}
          runQueues={project.runQueues}
          runSetRef={runSetRef}
          selectedRunName={selectedRunName}
          semanticLegendRef={sectionPart.semanticLegendRef}
          showArtifactCounts={showArtifactCounts}
          showLogCounts={showLogCounts}
          title={title}
        />
      )}
      <PanelsQuickAddContextProvider>
        <PanelBankWidthContextProvider
          panelBankWidth={panelBankWidth}
          setPanelBankWidth={setPanelBankWidth}>
          <WandbConfigContextProvider>
            <DefinedMetricContextProvider>
              <AutoSyncPanelBankContextProvider>
                <PanelBankSectionsScrollContextProvider>
                  {children}
                  {viewer?.userInfo?.bio?.includes('INSPECTOR') && (
                    <Inspector />
                  )}
                  <WorkspaceDrawer />
                </PanelBankSectionsScrollContextProvider>
              </AutoSyncPanelBankContextProvider>
            </DefinedMetricContextProvider>
          </WandbConfigContextProvider>
        </PanelBankWidthContextProvider>
      </PanelsQuickAddContextProvider>
    </RunSelectorResizingContextProvider>
  );
};
